import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { Schoolyear } from '@teetsh/app/src/types/schoolyear';
import { SCHOOLYEARS_KEY } from '@teetsh/app/src/queries/schoolyears/useSchoolyears';
import { SCHOOLYEARS_WITH_SCHOOLS_AND_CLASSROOMS_KEY } from '@teetsh/app/src/queries/schoolyears/useSchoolyearsWithSchoolsAndClassrooms';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { serializeSchoolyear } from '@teetsh/app/src/queries/schoolyears/serializer';
import { useTranslation } from 'react-i18next';
import { CreateSchoolyearOptions } from 'containers/MesAnneesScolaires/types';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';

function createSchoolyear(
  schoolyear: Schoolyear,
  options: CreateSchoolyearOptions,
  tz: string,
) {
  const requestURL = '/schoolyears';
  return request(requestURL, {
    method: 'POST',
    body: { ...serializeSchoolyear(schoolyear, tz), options },
  });
}

export function useCreateSchoolyear() {
  const { notifyError } = useNotification();
  const { t } = useTranslation(['schoolyearSettings']);
  const user = useUser();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      schoolyear,
      options,
    }: {
      schoolyear: Schoolyear;
      options: CreateSchoolyearOptions;
    }) => createSchoolyear(schoolyear, options, user.location),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SCHOOLYEARS_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [SCHOOLYEARS_WITH_SCHOOLS_AND_CLASSROOMS_KEY],
      });
    },
    onError: () => {
      notifyError({
        title: t('schoolyearSettings:messages.schoolyear.createError'),
      });
    },
  });
  return { createSchoolyear: mutateAsync, isPending };
}
